// defaultSeeds.ts
import type { SeedConfig } from '../generator/types';

export const defaultSeeds: SeedConfig = {
  mode: 'light',
  fontScale: 1,
  roles: {
    // PILOT MARKER (I-555): brand is a vivid magenta (not blue) on purpose, so
    // components already migrated to --nxt-* stand out clearly against the old
    // --nx-* navy. Revert to the real brand once migration is complete.
    brand: '#c026a0',
    neutral: '#76636f',
    danger: '#b5302a',
    success: '#0f9d6b',
    warning: '#b45309',
    info: '#c026a0',
  },
  fgMode: { brand: 'auto', danger: 'auto', success: 'auto', warning: 'auto', info: 'auto' },
};

/**
 * Selectable brand themes. The default (magenta, above) is the generated
 * base `:root`. Named catalog schemes and custom seeds are injected at
 * runtime as unlayered CSS — Connect never sets `data-brand`.
 *
 * A theme normally overrides only the brand hue + a **brand-touched neutral** (a
 * grey that leans toward the brand, exactly like the magenta theme's `#76636f`)
 * and `info` (which mirrors the brand); status roles stay shared, and every
 * remaining `--nxt-*` role token is neutral- or brand-derived, so it follows.
 *
 * A theme MAY override a status role too (any role in `SeedConfig['roles']`), and
 * has to when its brand hue sits on top of one. The ramp pins every solid fill to
 * the same lightness (L≈0.55), so two roles at a similar hue end up as the same
 * colour: `amber` used to render brand `#c1400a` next to danger `#c13c34` (OKLab
 * ΔE 3.3 — below what the eye separates) and warning `#b25105` (ΔE 4.1). Only the
 * theme knows which of its roles collide, so the override belongs here, not in the
 * generator. Keep such an override minimal and semantic: `danger` must still read
 * as an error, `warning` as a warning.
 */
export const brandThemes: Record<string, Partial<SeedConfig['roles']>> = {
  // Navy = a deep-blue accent carried by a cool, navy-tinted grey. The generator
  // normalizes brand-bg to a fixed lightness (L≈0.55) and scales each ramp by its
  // seed's CHROMA (lightness discarded — see ramp.ts), so a true deep navy is
  // impossible as the accent: at the navy hue it is either washed (low chroma) or
  // drifts to royal (high chroma). Instead the NEUTRAL carries the navy character
  // — #48597a lifts the grey's chroma (0.031 → ~0.058) so surfaces, text and
  // borders take a cool blue cast — which lets the brand stay a calm, deep blue
  // (#00539c → brand-bg #2d73bf) instead of a loud royal. Together they read navy
  // without going hazy or garish.
  navy: { brand: '#00539c', neutral: '#48597a', info: '#00539c' },
  // A warm burnt orange + a warm, orange-leaning taupe grey.
  //
  // The brand seed sits between the shared danger (H 27.6°) and warning (H 48.9°)
  // hues, and at the ramp's fixed solid lightness that made all three the same
  // colour (ΔE 3.3 / 4.1 — testers could not tell a brand button from an error).
  // So this theme retunes its own status hues away from the accent, keeping the
  // orange itself untouched:
  //   danger  → a cool crimson (ΔE 3.3 → 9.6), still unmistakably an error red
  //   warning → a golden amber-yellow (ΔE 4.1 → 12.9), and 17.3 from danger
  // `success` needs no override (ΔE 26.1, it is green). `info` mirrors the brand on
  // purpose, here as everywhere. ΔE 9.6 is the ceiling for danger while the accent
  // stays this orange: at one fixed lightness only hue and chroma separate them, and
  // pushing red further turns it pink. The magenta default reaches 16.9 because its
  // hue is 49° away from red to begin with.
  amber: {
    brand: '#c2410c',
    neutral: '#6f665c',
    info: '#c2410c',
    danger: '#a3164a',
    warning: '#977200',
  },
};
